/* eslint-disable jsx-a11y/anchor-is-valid */
/**
@format
@flow
*/
import React from 'react';
import styled from 'styled-components';
import { Heading, SubHeading } from '@ziynx/components/typography';
import Colors from '@ziynx/theme/colors';
import Link from '@ziynx/components/links';
import ListItem from '@ziynx/components/listItems';
import { withAppManager } from '@ziynx/components/utilities';
import type { Props as ListItemSquircleType } from '@ziynx/components/listItems/ListItemSquircle';
import type { TextLinkType } from '@ziynx/flow-types/links';
import type { ManagedProps } from '@ziynx/flow-types/appManager';
import CardBase from './CardBase';

type DetailType = $ReadOnly<{|
  label?: string,
  input?: string,
  link?: TextLinkType,
|}>;

export type Props = $ReadOnly<{|
  /**
   * Fixed width prop to activate sizes
   */
  fixedWidth?: boolean,
  /**
   * If fixedWidth is true, than sizes will be used
   */
  size?: 'extraSmall' | 'small' | 'medium' | 'large',
  /**
   * Company Image and Name, ListItemSquircle
   */
  placeholderText?: string,

  info?: ListItemSquircleType,

  details?: Array<DetailType>,

|}>;

const findWidth = (size) => {
  switch (size) {
  case 'extraSmall':
    return '288px';
  case 'small':
    return '382px';
  case 'medium':
    return '520px';
  case 'large':
    return '688px';
  default:
    return '100%';
  }
};

const InfoCard = styled(CardBase)`
  box-sizing: border-box;
  width: ${({ fixedWidth, width }) => (fixedWidth ? width : '100%')};
  max-width: 688px};
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const InfoHeader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ isLarge }) => (isLarge ? '32px' : '24px')};
`;

const StyledHeading = styled(Heading)`
  display: -webkit-box; 
  -webkit-line-clamp: 3; 
  -webkit-box-orient: vertical; 
  text-overflow: ellipsis;
  overflow: hidden;
  max-height: 72px;
`;

const StyledSubHeading = styled(SubHeading)`
  display: -webkit-box; 
  -webkit-line-clamp: 4; 
  -webkit-box-orient: vertical; 
  text-overflow: ellipsis;
  overflow: hidden;
  max-height: 80px;
`;

const StyledHeadline = styled(SubHeading)`
  margin-bottom: 4px;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
`;

const DetailSection = styled.div`
  margin-bottom: 32px;
  &:last-child {
    margin: 0;
  }
`;

/**
 * CardInfo component
 */
const CardCompanyDetails = (props: ManagedProps<Props>): React$Node => {
  const {
    fixedWidth,
    size,
    info,
    details = [],
    viewport,
    placeholderText,
    ...rest
  } = props;

  let isLarge = false;
  if (fixedWidth && size === 'large') isLarge = true;
  if (!fixedWidth) {
    switch (viewport) {
    case 'desktop':
      isLarge = true;
      break;
    case 'desktopSmall':
      isLarge = true;
      break;
    case 'tablet':
      isLarge = true;
      break;
    default:
      isLarge = false;
      break;
    }
  }

  const _renderDetails = () => {
    if (!details) return null;
    return details?.map((detail: DetailType, index: number): React$Node => (
      <DetailSection key={index}>
        <StyledHeadline
          large
          color={Colors.GrayDark}
        >
          {detail?.label}
        </StyledHeadline>
        {isLarge
          ? (
            <StyledHeading small>
              {detail?.input}
            </StyledHeading>
          )
          : (
            <StyledSubHeading medium>
              {detail?.input}
            </StyledSubHeading>
          )}
        {!detail?.input && detail?.label && detail?.link
        && (
          <SubHeading large>
            <Link onClick={detail?.link?.onClick} large variant="text">
              {detail?.link?.children || detail.label}
            </Link>
          </SubHeading>
        )}
      </DetailSection>
    ));
  };

  return (
    <InfoCard
      viewport={viewport}
      fixedWidth={fixedWidth}
      width={findWidth(size)}
      {...rest}
    >
      <ContentWrapper>
        <InfoHeader>
          <ListItem
            variant="squircle"
            {...info}
            size={isLarge ? 'medium' : 'extraSmall'}
            heading={isLarge ? info?.heading : undefined}
            text={!isLarge ? info?.heading : undefined}
            color={Colors.White}
            squircleBackgroundColor={Colors.GrayLight}
          />
        </InfoHeader>
        {_renderDetails()}
      </ContentWrapper>
    </InfoCard>
  );
};

export default (withAppManager(CardCompanyDetails): React$ComponentType<ManagedProps<Props>>);
